import { useState, useEffect } from 'react';
import { ArrowLeft, X, MapPin, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { motion } from 'motion/react';
import { Loader } from './ui/Loader';
import { LinkifiedText } from './ui/LinkifiedText';
import { projectId, API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { devLog } from '../utils/logger';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { VideoPlayer } from './VideoPlayer'; // ✅ CHANGED: Use VideoPlayer directly instead of VideoModal
import { FullscreenShell } from './layout/FullscreenShell';

interface PostDetailViewProps {
  post: {
    id: string;
    title: string;
    description: string;
    coverImageUrl?: string;
    imageUrl?: string;
    imageUrls?: string[];
    videoUrl?: string;
    videoId?: string; // ✅ NEW: Support Bunny CDN video ID
    user?: {
      name: string;
      location?: string;
      county?: string;
    };
    userId?: string;
    createdAt: string;
  };
  onClose: () => void;
  accessToken?: string | null;
  currentUserId?: string;
  isOwnPost?: boolean;
  /** When true, no view tracking or report; used for editor preview */
  isPreview?: boolean;
}

export function PostDetailView({ post, onClose, accessToken, currentUserId, isOwnPost, isPreview }: PostDetailViewProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen modal: hide header/footer while open
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [videoAspectRatio, setVideoAspectRatio] = useState<number | null>(null); // ✅ NEW: Track video aspect ratio
  
  // Touch/swipe state for mobile
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // ✅ NEW: Media loading state tracker
  const images = post.imageUrls || (post.imageUrl ? [post.imageUrl] : []);
  const coverImageToShow = post.coverImageUrl || null;
  const hasVideo = !!(post.videoId || post.videoUrl);
  const [mediaLoadingStates, setMediaLoadingStates] = useState({
    video: !hasVideo, // true if no video (already "loaded")
    images: images.map(() => false) // false for each image
  });

  // ✅ Check if all media is ready
  const allMediaReady = mediaLoadingStates.video &&
                         mediaLoadingStates.images.every(ready => ready);

  // ✅ Fallback: if video is in a hidden container (e.g. mobile/desktop responsive), loadedmetadata may never fire. Unblock UI after delay.
  useEffect(() => {
    if (!hasVideo) return;
    const t = setTimeout(() => {
      setMediaLoadingStates((prev) => {
        if (prev.video) return prev;
        return { ...prev, video: true };
      });
      setVideoAspectRatio((r) => (r !== null ? r : 16 / 9));
    }, 2500);
    return () => clearTimeout(t);
  }, [hasVideo]);

  // ✅ Fallback: if images never fire onLoad/onError (e.g. deferred loading, broken env), unblock after delay so we don't hang.
  useEffect(() => {
    if (images.length === 0) return;
    const t = setTimeout(() => {
      setMediaLoadingStates((prev) => {
        const allImagesReady = prev.images.every(Boolean);
        if (allImagesReady) return prev;
        return { ...prev, images: prev.images.map(() => true) };
      });
    }, 5000);
    return () => clearTimeout(t);
  }, [images.length]);

  // ✅ Callbacks from media components
  const handleVideoReady = (ratio: number) => {
    setVideoAspectRatio(ratio);
    setMediaLoadingStates(prev => ({ ...prev, video: true }));
    devLog('✅ Video metadata loaded and ready');
  };

  // Mark image(s) as ready. When one image loads or errors, we mark all slots ready so the UI
  // unblocks: only one <img> is rendered at a time (currentImageIndex), so the others never
  // get onLoad; marking all ready avoids infinite loading. Also handles failed loads (onError).
  const handleImageReady = (index: number) => {
    setMediaLoadingStates(prev => ({
      ...prev,
      images: prev.images.map(() => true)
    }));
    devLog(`✅ Image ${index + 1}/${images.length} ready (load or fallback)`);
  };

  // ✅ Browser history for back button
  useEffect(() => {
    // Push state when modal opens
    window.history.pushState({ modal: 'post-detail' }, '');
    
    const handlePopState = (e: PopStateEvent) => {
      // If history moved TO this modal state, keep it open.
      if (e.state?.modal === 'post-detail') return;
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);

  // Mobile: prosjektvisning shares the main scroll container; scroll to top when opening so user sees from top
  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Track post view when component mounts (skip in preview mode)
  useEffect(() => {
    if (isPreview) return;
    const trackView = async () => {
      try {
        await fetch(
          `${API_BASE}/post/views/${post.id}`,
          { method: 'POST' }
        );
      } catch (error) {
        devLog('Error tracking post view:', error);
      }
    };
    trackView();
  }, [post.id, isPreview]);
  
  // Support both old single image and new multiple images
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && hasMultipleImages) {
      nextImage();
    }
    if (isRightSwipe && hasMultipleImages) {
      prevImage();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const handleReportPost = async () => {
    if (!reportReason.trim()) {
      toast.error('Velg en årsak for rapporten');
      return;
    }
    
    try {
      const response = await fetch(
        `${API_BASE}/report/post`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            postId: post.id,
            reason: reportReason,
            description: reportDescription,
          }),
        }
      );

      if (response.ok) {
        setShowReportModal(false);
        setReportReason('');
        setReportDescription('');
        toast.success('Rapportering sendt', 'Takk for tilbakemeldingen');
      } else {
        const errorData = await response.json().catch(() => null);
        toast.error(errorData?.error || 'Kunne ikke sende rapport');
      }
    } catch (error) {
      devLog('Error reporting post:', error);
      toast.error('Noe gikk galt');
    }
  };

  // ✅ Check if video is portrait or landscape
  const isPortraitVideo = videoAspectRatio !== null && videoAspectRatio < 1;

  return (
    <>
      <FullscreenShell
        title={post.title || 'Prosjektdetaljer'}
        onClose={onClose}
        onBack={onClose}
        mobileHeaderRight={
          isPreview ? (
            <span className="text-xs font-medium text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full shrink-0">
              Forhåndsvisning
            </span>
          ) : null
        }
        contentClassName="px-0 py-0"
        overlayMotionProps={{
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: 0.18, ease: 'easeOut' },
        }}
      >
        <div className="relative post-detail-inner">
          {/* ✅ Loading overlay - shown until all media is ready */}
          {!allMediaReady && (
            <div className="absolute inset-0 bg-white rounded-none xl:rounded-3xl z-50 flex items-center justify-center">
              <div className="text-center">
                <Loader size="2xl" className="text-orange-600 mx-auto mb-3" />
                <p className="text-gray-600">Laster prosjekt...</p>
              </div>
            </div>
          )}

          <div className={`transition-opacity duration-300 ${allMediaReady ? 'opacity-100' : 'opacity-0'}`}>
            {/* Cover image (always top when available) */}
            {coverImageToShow ? (
              <div className="aspect-video w-full overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 rounded-none">
                <img
                  src={coverImageToShow}
                  alt={`${post.title} - Cover`}
                  className="w-full h-full object-cover"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
            ) : images.length > 0 ? (
              <div
                className="w-full max-h-[70vh] overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 rounded-none cursor-pointer flex items-center justify-center"
                onClick={() => setIsImageFullscreen(true)}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <img
                  src={images[currentImageIndex]}
                  alt={`${post.title} - Bilde ${currentImageIndex + 1}`}
                  className="w-full h-auto max-h-[70vh] object-contain hover:scale-105 transition-transform duration-300"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onLoad={() => handleImageReady(currentImageIndex)}
                  onError={() => handleImageReady(currentImageIndex)}
                />
              </div>
            ) : null}

            {/* Additional project images (separate from cover) */}
            {images.length > 0 && (
              <div className="px-8 pt-6">
                <div className="mb-3">
                  <h3 className="text-xl text-purple-900">Prosjektbilder</h3>
                </div>
                <div className="relative">
                  <div
                    className="w-full max-h-[70vh] overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl cursor-pointer flex items-center justify-center"
                    onClick={() => setIsImageFullscreen(true)}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                  >
                    <img
                      src={images[currentImageIndex]}
                      alt={`${post.title} - Tilleggsbilde ${currentImageIndex + 1}`}
                      className="w-full h-auto max-h-[70vh] object-contain hover:scale-105 transition-transform duration-300"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                      onLoad={() => handleImageReady(currentImageIndex)}
                      onError={() => handleImageReady(currentImageIndex)}
                    />
                  </div>

                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 xl:left-4 top-1/2 -translate-y-1/2 p-2 xl:p-3 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
                      >
                        <ChevronLeft className="w-4 h-4 xl:w-6 xl:h-6 text-gray-800" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 xl:right-4 top-1/2 -translate-y-1/2 p-2 xl:p-3 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
                      >
                        <ChevronRight className="w-4 h-4 xl:w-6 xl:h-6 text-gray-800" />
                      </button>
                    </>
                  )}

                  <p className="text-center text-sm text-gray-500 mt-2">
                    {currentImageIndex + 1} / {images.length}
                  </p>

                  {hasMultipleImages && (
                    <div className="absolute bottom-4 right-4 flex gap-2">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentImageIndex ? 'bg-white w-6' : 'bg-white bg-opacity-50'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-8">
              {hasVideo && isPortraitVideo ? (
                <div className="hidden xl:grid xl:grid-cols-[400px_1fr] xl:gap-8 xl:mb-6">
                  <div>
                    {post.videoId && (
                      <div
                        className="sticky top-8"
                        style={{ aspectRatio: videoAspectRatio !== null ? String(videoAspectRatio) : '9/16' }}
                      >
                        <VideoPlayer
                          videoId={post.videoId}
                          accessToken={accessToken}
                          className="w-full h-full rounded-2xl overflow-hidden"
                          onMetadataLoaded={handleVideoReady}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <h2 className="text-3xl sm:text-4xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 mb-4">
                      {post.title}
                    </h2>
                    <p className="text-gray-500 mb-6">Publisert {formatDate(post.createdAt)}</p>
                    {post.description && (
                      <div className="mb-6">
                        <h3 className="text-xl text-purple-900 mb-3">Beskrivelse</h3>
                        <LinkifiedText text={post.description} className="text-gray-700 text-lg leading-relaxed" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div className={hasVideo && isPortraitVideo ? 'xl:hidden' : ''}>
                <h2 className="text-3xl sm:text-4xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1 mb-4">
                  {post.title}
                </h2>
                <p className="text-gray-500 mb-6">Publisert {formatDate(post.createdAt)}</p>
                {post.description && (
                  <div className="mb-6">
                    <h3 className="text-xl text-purple-900 mb-3">Beskrivelse</h3>
                    <LinkifiedText text={post.description} className="text-gray-700 text-lg leading-relaxed" />
                  </div>
                )}

                {hasVideo && (
                  <div className="mb-6">
                    <h3 className="text-xl text-purple-900 mb-3">Video</h3>
                    {post.videoId ? (
                      <div
                        className="rounded-2xl overflow-hidden w-full"
                        style={{
                          aspectRatio:
                            videoAspectRatio !== null ? String(videoAspectRatio) : isPortraitVideo ? '9/16' : '16/9',
                        }}
                      >
                        <VideoPlayer
                          videoId={post.videoId}
                          accessToken={accessToken}
                          className="w-full h-full"
                          onMetadataLoaded={handleVideoReady}
                        />
                      </div>
                    ) : (
                      <div className="rounded-2xl overflow-hidden border-2 border-purple-200">
                        <video src={post.videoUrl} controls className="w-full h-auto">
                          Din nettleser stotter ikke video.
                        </video>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-6 border-t-2 border-purple-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-lg text-gray-900">{post.user?.name}</p>
                      {(post.user?.location || post.user?.county) && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <MapPin className="w-4 h-4" />
                          <span>{[post.user?.location, post.user?.county].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isOwnPost && accessToken && !isPreview && (
                    <button
                      onClick={() => setShowReportModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-700 border-2 border-orange-200 rounded-xl hover:bg-orange-100 transition-all"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      <span className="hidden sm:inline">Rapporter</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </FullscreenShell>

      {/* Fullscreen Image Viewer */}
      {isImageFullscreen && (
        <div 
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md z-[60] flex items-center justify-center p-4 pt-page-safe pb-safe"
          onClick={() => setIsImageFullscreen(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Simple close button in corner (like project cards) */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsImageFullscreen(false); }}
            className="absolute top-safe-offset right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all z-30"
            aria-label="Lukk fullskjerm"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>

          <div className="flex flex-col items-center max-w-full max-h-full">
            <img
              src={images[currentImageIndex]}
              alt={`${post.title} - Fullskjerm`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
            <p className="text-center text-white/90 text-lg mt-4">
              {currentImageIndex + 1} / {images.length}
            </p>
          </div>

          {hasMultipleImages && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-2 xl:left-4 top-1/2 -translate-y-1/2 p-2 xl:p-4 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
              >
                <ChevronLeft className="w-5 h-5 xl:w-8 xl:h-8 text-gray-800" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-2 xl:right-4 top-1/2 -translate-y-1/2 p-2 xl:p-4 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
              >
                <ChevronRight className="w-5 h-5 xl:w-8 xl:h-8 text-gray-800" />
              </button>
            </>
          )}
        </div>
      )}

      {/* Report Post Modal */}
      {showReportModal && (
        <div className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[70] p-4 pt-safe pb-safe">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Rapporter prosjekt
              </h3>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-2 hover:bg-purple-50 rounded-full transition-all"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">Årsak</label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Velg årsak</option>
                  <option value="spam">Spam</option>
                  <option value="inappropriate">Upassende innhold</option>
                  <option value="copyright">Brudd på opphavsrett</option>
                  <option value="misleading">Villedende informasjon</option>
                  <option value="other">Annet</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Beskrivelse (valgfritt)</label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Beskriv problemet..."
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleReportPost}
                  disabled={!reportReason}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send rapport
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
